import React, { useState, useEffect } from 'react';
import { useFormContext, Path, useWatch } from 'react-hook-form';
import { get } from 'lodash';
import { Search, Paperclip, X, Trash2 } from 'lucide-react';

import { OrderSchemaType, ItemPath } from '../../../order.schema';
import { useAttachmentHandler } from '../../../Hooks/useAttachmentHandler';
import SteelOrdersInfiniteSelect from '../../../components/SteelOrdersInfiniteSelect';
import { NumericInput } from '../../../components/NumericInput';

import {
  FormGroup,
  Input,
  Label,
  AttachmentList,
  AttachmentListItem,
  RemoveAttachmentButton,
  FormErrorMessage,
  AttachmentInputContainer,
  AttachmentTrigger,
  AttachmentPlaceholder,
  TrashIconContainer
} from '../../../style';
import {
  MaterialSearchInput,
  FormGridWithMargin,
  ObservationsFormGrid,
} from './FeedstockSection.style';

interface FeedstockSectionProps {
  itemPath: ItemPath;
}

const FeedstockSection: React.FC<FeedstockSectionProps> = ({ itemPath }) => {
  const { register, formState: { errors }, setValue, watch, control } = useFormContext<OrderSchemaType>();
  const { attachments, attachmentError, fileInputRef, handleFileSelectClick, handleFileChange, handleRemoveAttachment, handleRemoveAllAttachments } = useAttachmentHandler({ fieldPath: `${itemPath}.feedstock.anexos` });
  const feedstockPath = `${itemPath}.feedstock`;
  const API_BASE_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders`;
  const [selectedMaterials, setSelectedMaterials] = useState<{ id: number; name: string }[]>([]);
  const [selectedStock, setSelectedStock] = useState<{ id: number; name: string }[]>([]);
  


	useEffect(() => {
		const existingMaterialId = watch((`${itemPath}.materialId`) as any) as number | undefined;
		if (selectedMaterials.length > 0) {
			setValue(`${feedstockPath}.materiaPrima` as Path<OrderSchemaType>, selectedMaterials[0].name);
			setValue((`${itemPath}.materialId`) as any, selectedMaterials[0].id, { shouldDirty: true, shouldValidate: false });
			setSelectedStock([]);
			setValue(`${feedstockPath}.stockCodeId` as Path<OrderSchemaType>, undefined, { shouldDirty: true, shouldValidate: false });
			setValue(`${feedstockPath}.stockDescription` as Path<OrderSchemaType>, '', { shouldDirty: true, shouldValidate: false });
		} else {
			if (existingMaterialId === undefined || existingMaterialId === null) {
				setValue(`${feedstockPath}.materiaPrima` as Path<OrderSchemaType>, '');
				setValue((`${itemPath}.materialId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
				setSelectedStock([]);
				setValue((`${itemPath}.stockId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
				setValue(`${feedstockPath}.stockDescription` as Path<OrderSchemaType>, '', { shouldDirty: true, shouldValidate: false });
			}
		}
	}, [selectedMaterials, setValue, feedstockPath, itemPath, watch]);

  const watchedMaterialId = useWatch({ control, name: (`${itemPath}.materialId` as any) });
  const watchedMateriaPrima = useWatch({ control, name: (`${feedstockPath}.materiaPrima` as any) });

  useEffect(() => {
    const idRaw: any = watchedMaterialId as any;
    const id = typeof idRaw === 'number' ? idRaw : Number(idRaw);
    const nameRaw: any = watchedMateriaPrima as any;
    const name = typeof nameRaw === 'string' ? nameRaw : String(nameRaw || '');
    if (!isNaN(id) && id && selectedMaterials.length === 0) {
      setSelectedMaterials([{ id: Number(id), name: String(name || '') }]);
      if (!name || String(name).trim() === '') {
        (async () => {
          try {
            const res = await fetch(`${API_BASE_URL}/select-in-order/materials?id=${Number(id)}`);
            const data = await res.json();
            const arr = Array.isArray(data?.materials) ? data.materials : Array.isArray(data) ? data : [];
            const first = arr[0];
            const fetchedName = (first && typeof first === 'object') ? (first.name ?? '') : String(first ?? '');
            if (fetchedName) {
              setValue(`${feedstockPath}.materiaPrima` as Path<OrderSchemaType>, fetchedName, { shouldDirty: false, shouldValidate: false });
              setSelectedMaterials([{ id: Number(id), name: fetchedName }]);
              
            }
          } catch (e) { void e; }
        })();
      } else { void 0; }
    }
  }, [watchedMaterialId, watchedMateriaPrima, selectedMaterials.length, setValue, feedstockPath]);

  useEffect(() => {
    if (selectedStock.length > 0) {
      setValue((`${itemPath}.stockId`) as any, selectedStock[0].id, { shouldDirty: true, shouldValidate: false });
      setValue(`${feedstockPath}.stockDescription` as Path<OrderSchemaType>, selectedStock[0].name, { shouldDirty: true, shouldValidate: false });
    }
  }, [selectedStock, setValue, feedstockPath, itemPath]);

  const watchedStockId = useWatch({ control, name: (`${itemPath}.stockId`) as any });
  const watchedStockDescription = useWatch({ control, name: `${feedstockPath}.stockDescription` as any });
  const watchedQuantidade = useWatch({ control, name: `${feedstockPath}.quantidade` as any });

  useEffect(() => {
    const idRaw: any = watchedStockId as any;
    const id = typeof idRaw === 'number' ? idRaw : Number(idRaw);
    const nameRaw: any = watchedStockDescription as any;
    const name = typeof nameRaw === 'string' ? nameRaw : String(nameRaw || '');
    if (!isNaN(id) && id && selectedStock.length === 0) {
      setSelectedStock([{ id: Number(id), name: String(name || '') }]);
      if (!name || String(name).trim() === '') {
        (async () => {
          try {
            const materialId = selectedMaterials[0]?.id || watch((`${itemPath}.materialId`) as any);
            if (materialId) {
              const res = await fetch(`${API_BASE_URL}/select-in-order/stocks?materialId=${materialId}&id=${Number(id)}`);
              const data = await res.json();
              const arr = Array.isArray(data?.stocks) ? data.stocks : Array.isArray(data) ? data : [];
              if (arr.length > 0) {
                const stockItem = arr[0];
                const stockName = stockItem.description || stockItem.name || '';
                setValue(`${feedstockPath}.stockDescription` as Path<OrderSchemaType>, stockName, { shouldDirty: true, shouldValidate: false });
                setSelectedStock([{ id: Number(id), name: stockName }]);
              }
            }
          } catch (e) { void e; }
        })();
      } else { void 0; }
    }
  }, [watchedStockId, watchedStockDescription, selectedStock.length, setValue, feedstockPath, selectedMaterials, watch, itemPath, API_BASE_URL]);

  useEffect(() => {
    const qty = Number(watchedQuantidade);
    if (!isNaN(qty) && qty > 0) {
      setValue((`${itemPath}.quantity`) as any, qty, { shouldDirty: true, shouldValidate: false });
    } else if (watchedQuantidade === '' || watchedQuantidade === undefined || watchedQuantidade === null) {
      setValue((`${itemPath}.quantity`) as any, undefined, { shouldDirty: true, shouldValidate: false });
    }
  }, [watchedQuantidade, setValue, itemPath]);

  return (
    <>
      <FormGridWithMargin columns={4}>
        <SteelOrdersInfiniteSelect
          label="Matéria Prima"
          items={selectedMaterials}
          setItems={setSelectedMaterials}
          endpoint="materials"
          placeholder="Procure por nome ou código"
          onCleared={() => {
            setValue((`${itemPath}.materialId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
            setValue(`${feedstockPath}.materiaPrima` as Path<OrderSchemaType>, '', { shouldDirty: true, shouldValidate: false });
            setSelectedStock([]);
            setValue((`${itemPath}.stockId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
            setValue(`${feedstockPath}.stockDescription` as Path<OrderSchemaType>, '', { shouldDirty: true, shouldValidate: false });
          }}
        />
        <SteelOrdersInfiniteSelect
          label="Estoque"
          items={selectedStock}
          setItems={setSelectedStock}
          endpoint="stocks"
          placeholder="Selecione um item do estoque"
          searchPlaceholder={selectedMaterials[0]?.id ? 'Nenhum estoque encontrado' : 'Selecione um material primeiro'}
          isDisabled={!selectedMaterials[0]?.id}
          additionalParams={selectedMaterials[0]?.id ? { materialId: selectedMaterials[0].id } : {}}
          onCleared={() => {
            setValue((`${itemPath}.stockId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
            setValue(`${feedstockPath}.stockDescription` as Path<OrderSchemaType>, '', { shouldDirty: true, shouldValidate: false });
          }}
          key={selectedMaterials[0]?.id ? `stocks-${selectedMaterials[0].id}` : 'stocks-disabled'}
        />
        <FormGroup>
          <Label>Quantidade</Label>
          <NumericInput
            name={`${feedstockPath}.quantidade`}
            value={(watch(`${feedstockPath}.quantidade` as any) as any) || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(`${feedstockPath}.quantidade` as any, e.target.value, { shouldDirty: true })}
            onBlur={() => {}}
            hasError={!!get(errors, `${feedstockPath}.quantidade`)}
          />
        </FormGroup>
        <FormGroup>
          <Label>Arquivo</Label>
          <AttachmentInputContainer>
            <AttachmentTrigger onClick={handleFileSelectClick} hasError={!!attachmentError}>
              <Paperclip size={16} />
              <AttachmentPlaceholder>Clique para anexar arquivo</AttachmentPlaceholder>
            </AttachmentTrigger>
            {attachments.length > 0 && (
              <TrashIconContainer
                as="button"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRemoveAllAttachments();
                }}
              >
                <Trash2 size={16} />
              </TrashIconContainer>
            )}
          </AttachmentInputContainer>
          {attachments.length > 0 && (
            <AttachmentList>
              {attachments.map((file, index) => (
                <AttachmentListItem key={index}>
                  {file.name}
                  <RemoveAttachmentButton
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveAttachment(index);
                    }}
                  >
                    <X size={12} />
                  </RemoveAttachmentButton>
                </AttachmentListItem>
              ))}
            </AttachmentList>
          )}
          <input
            type="file"
            multiple
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            accept=".pdf,.xml,.doc,.docx,.xls,.xlsx,.jpeg,.jpg,.png"
          />
          {attachmentError && <FormErrorMessage>{attachmentError}</FormErrorMessage>}
        </FormGroup>
      </FormGridWithMargin>

      <ObservationsFormGrid>
        <FormGroup>
          <Label>Observações</Label>
          <Input {...register(`${feedstockPath}.observacoes` as Path<OrderSchemaType>)} placeholder="Digite aqui" />
        </FormGroup>
      </ObservationsFormGrid>
    </>
  );
};

export default FeedstockSection;